import { getEvalUserMedia } from "./evalVideoEncoding";

const PRIMED_STORAGE_KEY = "smarttt.evalCamera.permissionPrimed";

/** Session-held MediaStream — kept alive after the tap so iOS won't re-prompt mid-exercise. */
let heldStream = null;
let heldListeners = new Set();

export function isEvalCameraPermissionPrimed() {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(PRIMED_STORAGE_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  return Boolean(getHeldEvalCameraStream());
}

function markEvalCameraPermissionPrimed() {
  try {
    window.sessionStorage.setItem(PRIMED_STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function queryEvalCameraPermissionState() {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return Promise.resolve(null);
  }
  return navigator.permissions
    .query({ name: "camera" })
    .then((status) => status?.state || null)
    .catch(() => null);
}

function notifyHeldListeners() {
  for (const fn of heldListeners) {
    try {
      fn(heldStream);
    } catch {
      /* ignore */
    }
  }
}

export function subscribeHeldEvalCameraStream(listener) {
  if (typeof listener !== "function") return () => {};
  heldListeners.add(listener);
  return () => heldListeners.delete(listener);
}

export function getHeldEvalCameraStream() {
  if (!heldStream) return null;
  const live = heldStream.getVideoTracks().some((t) => t.readyState === "live");
  if (!live) {
    heldStream = null;
    return null;
  }
  return heldStream;
}

/**
 * Keep this stream for the live-training session so later getUserMedia isn't needed.
 */
export function holdEvalCameraStream(stream) {
  if (!stream) return null;
  if (heldStream && heldStream !== stream) {
    try {
      heldStream.getTracks().forEach((t) => {
        if (stream.getTracks().includes(t)) return;
        t.stop();
      });
    } catch {
      /* ignore */
    }
  }
  heldStream = stream;
  markEvalCameraPermissionPrimed();
  // Keep tracks "live" but muted when parked — avoids mid-rally permission prompts.
  try {
    heldStream.getVideoTracks().forEach((t) => {
      t.enabled = true;
    });
  } catch {
    /* ignore */
  }
  notifyHeldListeners();
  return heldStream;
}

/** Soft-park: disable tracks (less heat) without releasing permission. */
export function parkHeldEvalCameraStream() {
  const stream = getHeldEvalCameraStream();
  if (!stream) return;
  try {
    stream.getTracks().forEach((t) => {
      t.enabled = false;
    });
  } catch {
    /* ignore */
  }
}

export function resumeHeldEvalCameraStream() {
  const stream = getHeldEvalCameraStream();
  if (!stream) return null;
  try {
    stream.getTracks().forEach((t) => {
      t.enabled = true;
    });
  } catch {
    /* ignore */
  }
  return stream;
}

/** Fully release — only when leaving live training / disabling capture. */
export function releaseHeldEvalCameraStream() {
  if (!heldStream) return;
  try {
    heldStream.getTracks().forEach((t) => t.stop());
  } catch {
    /* ignore */
  }
  heldStream = null;
  notifyHeldListeners();
}

/**
 * Request camera access once while the user can still tap (join / enter session).
 * Keeps the stream held for the session so exercise start does not re-prompt.
 */
export async function primeEvalCameraPermission(options = {}) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { ok: false, denied: false, reason: "unsupported" };
  }
  const existing = getHeldEvalCameraStream();
  if (existing) {
    markEvalCameraPermissionPrimed();
    return { ok: true, alreadyPrimed: true, stream: existing };
  }
  const queried = await queryEvalCameraPermissionState();
  if (queried === "granted" && isEvalCameraPermissionPrimed()) {
    // Permission bit is set but we stopped the stream earlier — reopen now if
    // we still have a user gesture (banner / join). Otherwise wait for hold.
  }
  try {
    const facing =
      options?.facingMode === "user" || options?.facingMode === "environment"
        ? options.facingMode
        : undefined;
    const stream = await getEvalUserMedia(facing ? { facingMode: facing } : {});
    holdEvalCameraStream(stream);
    // Soft-park until prewarm/recording needs frames (still holds permission).
    parkHeldEvalCameraStream();
    return { ok: true, stream };
  } catch (err) {
    const denied =
      err?.name === "NotAllowedError" ||
      err?.name === "PermissionDeniedError";
    return {
      ok: false,
      denied,
      reason: err?.message || String(err),
    };
  }
}
